/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * useSignOut
 * ----------
 * The feed's logout. Same sequence as OrganizerSidebar: server logout, then
 * clear the stored session, roles and any unfinished-payment notes, signal
 * NotificationProvider, and go to /sign-in.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { store, retrieve } from '../../utils/storage_helper';
import { clearRoleState } from '../../utils/roles';
import { forgetAllPending } from '../athlohive/event-detail/pendingPayment';
import { purgeUser } from '../../pages/feed/routeCache';
import { feedUserFrom, resetRouteCacheMemory } from '../../pages/feed/useFeedData';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export default function useSignOut() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const signOut = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await apiHelper.apiCall('/arcee/user/logout/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        let body = null;
        try { body = await response.json(); } catch (e) { body = null; }
        setError((body && (body.error || body.message || body.detail)) || `Logout failed (status ${response.status})`);
        return;
      }
      // Nothing of this athlete may outlive their session on a shared computer.
      // Routes are the sensitive part: a GPS trace shows where someone lives.
      // Read the id before the session is cleared, then drop their stored
      // routes and everything React Query is holding in memory.
      const { userId } = feedUserFrom(retrieve('userData'));
      if (userId !== null && userId !== undefined) purgeUser(userId);
      resetRouteCacheMemory();
      store('userData', null);
      forgetAllPending();
      store('access_token', null);
      clearRoleState();
      queryClient.clear();
      window.dispatchEvent(new Event('arcee:auth-change'));
      navigate('/sign-in', { replace: true });
    } catch (err) {
      setError(err && err.type === 'session_expired'
        ? 'Your session has expired. Please sign in again.'
        : (err && err.message) || 'Logout failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return { signOut, busy, error, clearError: () => setError(null) };
}
